import { useState, useEffect } from 'preact/hooks';
import { touchPointsApi } from '@/api/touchPoints';
import { useToast } from './useToast';
export function useTouchPointManager({ workspaceId, campaignId, webId, typeFilters, isCampaignLevel = false, }) {
    const [touchPoints, setTouchPoints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const { showSuccess, showError, showErrorUnless422 } = useToast();
    // Fetch touchpoints
    const fetchTouchPoints = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = isCampaignLevel && campaignId
                ? await touchPointsApi.getTouchPointsByCampaign(workspaceId, campaignId, typeFilters)
                : webId
                    ? await touchPointsApi.getTouchPointsByWeb(workspaceId, webId, typeFilters)
                    : { error: 'Invalid configuration', data: null };
            if (response.error) {
                setError(response.error);
                setTouchPoints([]);
            }
            else {
                setTouchPoints(response.data || []);
            }
        }
        catch (err) {
            setError('Failed to load touchpoints');
            setTouchPoints([]);
        }
        finally {
            setLoading(false);
        }
    };
    // Auto-fetch on mount and when dependencies change
    useEffect(() => {
        fetchTouchPoints();
    }, [workspaceId, campaignId, webId, typeFilters?.join(',')]);
    // Create touchpoint
    const createTouchPoint = async (dto, successMessage = 'Touch Point Created', file) => {
        setSaving(true);
        try {
            const response = await touchPointsApi.createTouchPoint(workspaceId, dto, file);
            if (response.error) {
                showErrorUnless422('Creation Failed', response);
                return { success: false, error: response.error };
            }
            // Add to local state
            setTouchPoints(prev => [...prev, response.data]);
            showSuccess(successMessage, 'The touch point was created successfully.');
            return { success: true, data: response.data };
        }
        catch (err) {
            showError('Error', 'An unexpected error occurred.');
            return { success: false, error: 'Unexpected error' };
        }
        finally {
            setSaving(false);
        }
    };
    // Update touchpoint
    const updateTouchPoint = async (touchPointId, dto, successMessage = 'Touch Point Updated', file) => {
        setSaving(true);
        try {
            const response = await touchPointsApi.updateTouchPoint(workspaceId, touchPointId, dto, file);
            if (response.error) {
                showErrorUnless422('Update Failed', response);
                return { success: false, error: response.error };
            }
            // Update local state
            setTouchPoints(prev => prev.map(tp => (tp.Id === touchPointId ? response.data : tp)));
            showSuccess(successMessage, 'The touch point was updated successfully.');
            return { success: true, data: response.data };
        }
        catch (err) {
            showError('Error', 'An unexpected error occurred.');
            return { success: false, error: 'Unexpected error' };
        }
        finally {
            setSaving(false);
        }
    };
    // Delete touchpoint
    const deleteTouchPoint = async (touchPointId, touchPointName, successMessage = 'Touch Point Deleted') => {
        setDeleting(true);
        try {
            const response = await touchPointsApi.deleteTouchPoint(workspaceId, touchPointId);
            if (response.error) {
                showErrorUnless422('Deletion Failed', response);
                return { success: false, error: response.error };
            }
            // Remove from local state
            setTouchPoints(prev => prev.filter(tp => tp.Id !== touchPointId));
            showSuccess(successMessage, `"${touchPointName}" was deleted successfully.`);
            return { success: true };
        }
        catch (err) {
            showError('Error', 'An unexpected error occurred.');
            return { success: false, error: 'Unexpected error' };
        }
        finally {
            setDeleting(false);
        }
    };
    return {
        touchPoints,
        loading,
        error,
        saving,
        deleting,
        fetchTouchPoints,
        createTouchPoint,
        updateTouchPoint,
        deleteTouchPoint,
    };
}
